import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Briefcase,
  Send,
  Users,
  Sparkles,
  ShieldCheck,
  Heart,
  Award,
  MessageCircle,
  CheckCircle2,
  MapPin,
  ChevronRight,
  Calendar,
  FileUp,
} from 'lucide-react';
import { jobsApi, POST_TYPE_LABELS, EMPLOYMENT_LABELS, buildJobPostingJsonLd } from './careersApi';

const WHATSAPP_LINK =
  'https://wa.me/2250778415277?text=Bonjour%20Pharmacie%20SADA%2C%20je%20souhaite%20d%C3%A9poser%20une%20candidature%20spontan%C3%A9e.';

const POSTS = [
  { value: 'auxiliaire', label: 'Auxiliaire en pharmacie', icon: Users },
  { value: 'pharmacien', label: 'Pharmacien(ne) diplômé(e)', icon: Award },
  { value: 'administratif', label: 'Administratif / Gestion', icon: Briefcase },
  { value: 'stagiaire', label: 'Stagiaire', icon: Sparkles },
  { value: 'livreur', label: 'Livreur / Coursier', icon: MessageCircle },
  { value: 'autre', label: 'Autre poste', icon: Heart },
];

const VALEURS = [
  { icon: ShieldCheck, title: 'Rigueur pharmaceutique',
    text: 'Respect strict du code de déontologie, formation continue et qualité de service au patient.' },
  { icon: Heart, title: 'Humain d\'abord',
    text: 'Chaque collaborateur est accompagné dans son évolution et considéré comme un maillon essentiel.' },
  { icon: Sparkles, title: 'Innovation quotidienne',
    text: 'Outils digitaux (SADA Academy, SADASkinCare, SADAHealth) qui rendent le métier plus stimulant.' },
];

const RecrutementPage = ({ onBack, onNavigate, initialJobSlug }) => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    jobsApi
      .listPublic()
      .then((res) => {
        if (!cancelled) setJobs(res.items || []);
      })
      .catch(() => {
        if (!cancelled) setJobs([]);
      })
      .finally(() => {
        if (!cancelled) setJobsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Injecte un JSON-LD ItemList couvrant toutes les offres publiées
  useEffect(() => {
    if (!jobs.length) return undefined;
    const items = jobs.map((j, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: buildJobPostingJsonLd(j, `https://phciesada.org/recrutement/${j.slug}`),
    }));
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.jobsLd = '1';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: items,
    });
    document.head.appendChild(script);
    return () => {
      script.parentNode?.removeChild(script);
    };
  }, [jobs]);

  // Highlight visuel du formulaire quand l'utilisateur arrive depuis un TDR
  const [formHighlighted, setFormHighlighted] = useState(false);

  useEffect(() => {
    const handler = (ev) => {
      const { slug, postType } = ev.detail || {};
      setForm((f) => ({
        ...f,
        job_slug: slug || f.job_slug,
        post: postType || f.post,
      }));
      // Scroll robuste vers le formulaire (backup si le setTimeout de
      // PharmacyApp n'a pas suffi à cause du remount).
      setTimeout(() => {
        const el = document.getElementById('formulaire');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setFormHighlighted(true);
        window.setTimeout(() => setFormHighlighted(false), 2400);
      }, 50);
      toast.success('Poste sélectionné ✓ Remplissez le formulaire ci-dessous 👇', {
        duration: 4000,
      });
    };
    window.addEventListener('sada:prefill-job', handler);
    return () => window.removeEventListener('sada:prefill-job', handler);
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Nous rejoindre — Recrutement Pharmacie SADA à Abidjan';

    let m = document.querySelector('meta[name="description"]');
    const prevMetaContent = m ? m.getAttribute('content') : null;
    if (!m) {
      m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
    }
    m.setAttribute(
      'content',
      'Rejoignez la Pharmacie SADA à Cocody Angré. Auxiliaire, pharmacien, administratif, stagiaire : déposez votre candidature spontanée en ligne.',
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://phciesada.org/recrutement');

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsMeta ? robotsMeta.getAttribute('content') : null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'index, follow');

    return () => {
      document.title = prevTitle;
      if (m && prevMetaContent !== null) m.setAttribute('content', prevMetaContent);
      if (canonical && prevCanonical !== null) canonical.setAttribute('href', prevCanonical);
      else if (canonical && prevCanonical === null) canonical.parentNode?.removeChild(canonical);
      if (robotsMeta && prevRobots !== null) robotsMeta.setAttribute('content', prevRobots);
      else if (robotsMeta && prevRobots === null) robotsMeta.parentNode?.removeChild(robotsMeta);
    };
  }, []);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    post: 'auxiliaire',
    message: '',
    cv_link: '',
    job_slug: initialJobSlug || '',
    honeypot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) {
      toast.error('Merci d\'indiquer votre nom complet.');
      return;
    }
    if (form.phone.trim().length < 6) {
      toast.error('Merci d\'indiquer un numéro de téléphone valide.');
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error('Merci de décrire brièvement votre parcours ou motivation.');
      return;
    }
    // Validation fichiers côté client (double-sécurité avec le backend)
    const MAX = 5 * 1024 * 1024;
    const ALLOWED = /\.(pdf|doc|docx)$/i;
    for (const [label, f] of [['CV', cvFile], ['Lettre de motivation', coverFile]]) {
      if (!f) continue;
      if (!ALLOWED.test(f.name)) {
        toast.error(`${label} : format refusé (PDF, DOC ou DOCX uniquement).`);
        return;
      }
      if (f.size > MAX) {
        toast.error(`${label} : dépasse 5 Mo.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('phone', form.phone);
      fd.append('post', form.post);
      fd.append('message', form.message);
      if (form.email) fd.append('email', form.email);
      if (form.cv_link) fd.append('cv_link', form.cv_link);
      if (form.job_slug) fd.append('job_slug', form.job_slug);
      if (form.honeypot) fd.append('honeypot', form.honeypot);
      if (cvFile) fd.append('cv_file', cvFile);
      if (coverFile) fd.append('cover_letter_file', coverFile);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/careers/applications`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setDone(true);
      toast.success('Candidature envoyée à la Pharmacie SADA.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF6F1] via-white to-[#FAF6F1]" data-testid="page-recrutement">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8DDD4] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-[#5D4E37] hover:bg-[#F5EDE4]"
            data-testid="recrutement-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="font-bold text-[#5D4E37] text-sm sm:text-base">
            Nous rejoindre · Pharmacie SADA
          </h1>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5D4E37]/5 via-transparent to-[#C9A086]/10 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 pt-10 pb-6 sm:pt-14 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5D4E37] text-white text-xs font-medium mb-4">
              <Briefcase className="w-3.5 h-3.5" />
              Nous rejoindre
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#5D4E37] leading-tight">
              Rejoignez une pharmacie humaine, moderne et engagée
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#78716C] leading-relaxed max-w-2xl mx-auto">
              La Pharmacie Saint Ange Des Amis, située à Cocody Angré 7e Tranche,
              étudie régulièrement les candidatures spontanées pour renforcer son équipe.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VALEURS */}
      <section className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {VALEURS.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
              >
                <Card className="h-full border-[#E8DDD4] bg-white">
                  <CardContent className="p-5">
                    <div className="w-11 h-11 rounded-xl bg-[#5D4E37]/10 text-[#5D4E37] flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h4 className="font-semibold text-[#5D4E37] mb-1">{v.title}</h4>
                    <p className="text-sm text-[#78716C] leading-relaxed">{v.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SECTION 1 — POSTES DISPONIBLES */}
      <section className="max-w-6xl mx-auto px-4 py-4 sm:py-8" id="postes" data-testid="recrutement-postes">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5D4E37]/10 text-[#5D4E37] text-xs font-semibold mb-3">
            <span>1</span>
            <span>·</span>
            <span>Postes disponibles</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">
            Postes actuellement à pourvoir
          </h3>
          <p className="text-sm text-[#78716C] mt-2">
            Termes de Référence détaillés · candidatez en un clic
          </p>
        </div>
        {jobsLoading ? (
          <div className="text-center text-[#78716C] py-8">Chargement des offres…</div>
        ) : jobs.length === 0 ? (
          <div className="text-center text-[#78716C] py-8 italic">
            Aucun poste ouvert actuellement. Envoyez-nous une candidature spontanée ci-dessous.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job, idx) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.08 }}
                className="h-full"
              >
                <Card
                  className="h-full border-[#E8DDD4] bg-white hover:shadow-lg hover:border-[#5D4E37]/40 transition-all cursor-pointer flex flex-col"
                  onClick={() => onNavigate?.(`recrutement:${job.slug}`)}
                  data-testid={`job-card-${job.slug}`}
                >
                  <CardContent className="p-5 flex-1 flex flex-col">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge className="bg-[#5D4E37] text-white hover:bg-[#4A3E2C]">
                        {EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}
                      </Badge>
                      <Badge variant="outline" className="border-[#C9A086] text-[#5D4E37]">
                        {POST_TYPE_LABELS[job.post_type] || job.post_type}
                      </Badge>
                    </div>
                    <h4 className="font-serif font-bold text-lg text-[#5D4E37] mb-2 leading-tight">
                      {job.title}
                    </h4>
                    <p className="text-sm text-[#78716C] leading-relaxed line-clamp-4 flex-1">
                      {job.description_short}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-[#78716C] mt-3">
                      <MapPin className="w-3 h-3" />
                      <span>{job.city || 'Abidjan'}</span>
                      {job.deadline_iso && (
                        <>
                          <span className="mx-1">·</span>
                          <Calendar className="w-3 h-3" />
                          <span>Jusqu&apos;au {(job.deadline_iso || '').slice(0, 10)}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E8DDD4]">
                      <span className="text-sm text-[#5D4E37] font-medium inline-flex items-center gap-1 hover:underline">
                        Lire les détails <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* SECTION 2 — POSTULER SPONTANÉMENT */}
      <section className="max-w-3xl mx-auto px-4 pb-12" id="formulaire">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5D4E37]/10 text-[#5D4E37] text-xs font-semibold mb-3">
            <span>2</span>
            <span>·</span>
            <span>Postuler spontanément</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#5D4E37]">
            Aucun poste ne correspond ? Écrivez-nous
          </h3>
          <p className="text-sm text-[#78716C] mt-2">
            Envoyez votre CV et votre lettre de motivation directement à la Pharmacie SADA.
          </p>
        </div>
        <Card
          className={`border-[#E8DDD4] shadow-sm transition-all duration-500 ${
            formHighlighted
              ? 'ring-4 ring-[#5D4E37]/40 border-[#5D4E37] shadow-2xl scale-[1.01]'
              : ''
          }`}
        >
          <CardContent className="p-6 sm:p-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
                data-testid="recrutement-success"
              >
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-serif font-bold text-[#5D4E37]">Merci !</h3>
                <p className="text-[#78716C] mt-3 max-w-md mx-auto">
                  Votre candidature a bien été transmise à l&apos;équipe SADA.
                  Nous revenons vers vous si votre profil correspond à un besoin actuel ou à venir.
                </p>
                <div className="flex flex-wrap gap-3 justify-center mt-6">
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full px-6">
                      <MessageCircle className="w-4 h-4 mr-2" /> Nous écrire sur WhatsApp
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    className="border-[#5D4E37] text-[#5D4E37] hover:bg-[#5D4E37] hover:text-white rounded-full px-6"
                    onClick={onBack}
                  >
                    Retour à l&apos;accueil
                  </Button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="recrutement-form">
                <h3 className="text-xl font-serif font-bold text-[#5D4E37]">
                  Votre candidature
                </h3>
                <p className="text-sm text-[#78716C]">
                  Les champs marqués <span className="text-red-500">*</span> sont obligatoires.
                </p>

                {/* Chip poste sélectionné (visible si venue depuis un TDR) */}
                {form.job_slug && (
                  <div
                    className="flex items-center justify-between gap-3 bg-[#F5EDE4] border border-[#C9A086] rounded-lg px-3 py-2"
                    data-testid="selected-job-chip"
                  >
                    <div className="flex items-center gap-2 text-sm text-[#5D4E37]">
                      <Briefcase className="w-4 h-4" />
                      <span>
                        Candidature pour :{' '}
                        <strong className="font-semibold">{form.job_slug}</strong>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, job_slug: '' }))
                      }
                      className="text-xs text-[#78716C] hover:text-[#5D4E37] underline"
                      data-testid="clear-selected-job"
                    >
                      changer
                    </button>
                  </div>
                )}

                {/* Honeypot caché */}
                <input
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={form.honeypot}
                  onChange={update('honeypot')}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                  aria-hidden="true"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm text-[#5D4E37] font-medium">Nom complet <span className="text-red-500">*</span></span>
                    <input
                      type="text"
                      required
                      value={form.full_name}
                      onChange={update('full_name')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                      placeholder="Ex : Aya Konan"
                      data-testid="recrutement-input-name"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm text-[#5D4E37] font-medium">Téléphone <span className="text-red-500">*</span></span>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={update('phone')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                      placeholder="+225 07 XX XX XX XX"
                      data-testid="recrutement-input-phone"
                    />
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium">Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                      placeholder="nom@exemple.com"
                      data-testid="recrutement-input-email"
                    />
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium">Poste souhaité <span className="text-red-500">*</span></span>
                    <select
                      required
                      value={form.post}
                      onChange={update('post')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                      data-testid="recrutement-input-post"
                    >
                      {POSTS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium flex items-center gap-2">
                      <FileUp className="w-4 h-4" />
                      CV (PDF, DOC ou DOCX — 5 Mo max) <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      required
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                      className="mt-1 w-full border rounded-md px-3 py-2 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#5D4E37] file:text-white hover:file:bg-[#4A3E2C] file:cursor-pointer"
                      data-testid="recrutement-input-cv-file"
                    />
                    {cvFile && (
                      <span className="text-xs text-[#78716C] mt-1 block">
                        ✓ {cvFile.name} ({Math.round(cvFile.size / 1024)} Ko)
                      </span>
                    )}
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium flex items-center gap-2">
                      <FileUp className="w-4 h-4" />
                      Lettre de motivation (facultatif)
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                      className="mt-1 w-full border rounded-md px-3 py-2 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#5D4E37]/70 file:text-white hover:file:bg-[#5D4E37] file:cursor-pointer"
                      data-testid="recrutement-input-cover-file"
                    />
                    {coverFile && (
                      <span className="text-xs text-[#78716C] mt-1 block">
                        ✓ {coverFile.name} ({Math.round(coverFile.size / 1024)} Ko)
                      </span>
                    )}
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium">Lien alternatif (Google Drive, LinkedIn…) — si vous n&apos;avez pas de PDF sous la main</span>
                    <input
                      type="url"
                      value={form.cv_link}
                      onChange={update('cv_link')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                      placeholder="https://..."
                      data-testid="recrutement-input-cv"
                    />
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="text-sm text-[#5D4E37] font-medium">Votre message <span className="text-red-500">*</span></span>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={update('message')}
                      className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37] resize-y"
                      placeholder="Présentez brièvement votre parcours, votre disponibilité et vos motivations."
                      data-testid="recrutement-input-message"
                    />
                  </label>
                </div>

                <div className="flex flex-wrap gap-3 justify-end pt-2">
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-5"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" /> Écrire sur WhatsApp
                    </Button>
                  </a>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white rounded-full px-6"
                    data-testid="recrutement-submit-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Envoi…' : 'Envoyer ma candidature'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Lien vers partenariats */}
        <div className="text-center mt-6 text-sm text-[#78716C]">
          Vous représentez une organisation ?{' '}
          <button
            type="button"
            onClick={() => onNavigate?.('partenariats')}
            className="text-[#5D4E37] font-medium underline underline-offset-2 hover:text-[#3E3325]"
            data-testid="recrutement-link-partenariats"
          >
            Découvrez nos partenariats
          </button>
          .
        </div>
      </section>
    </div>
  );
};

export default RecrutementPage;
